// PostHog browser initialisation, bundled through esbuild rather than pasted in as a CDN
// snippet so the SDK version is pinned in yarn.lock like every other dependency.
//
// This file is deliberately thin: it owns the posthog-js import and nothing else. The rules
// about what we capture live in posthog_pageviews.js, which is testable without a browser.
//
// Importing this module registers the listener and does nothing more. PostHog itself is not
// initialised until a page renders the settings element, so a Turbo visit that carries this
// already-loaded bundle from the marketing site into an authenticated page sends no event.

import posthog from "posthog-js"

import { fillDistinctIdFields } from "./posthog_attribution"
import { CONSENT_STATUS_EVENT, wireConsentBridge } from "./consent"
import { postHogInitOptions, trackTurboPageviews } from "./posthog_pageviews"
import { applyReplayState, autocaptureAllowed } from "./session_replay"

// The banner never sees this object. It dispatches DOM events and the bridge below turns them
// into these three calls, which is what keeps posthog-js out of the application bundle and off
// every authenticated screen (analytics/consent.js explains the split).
const consentApi = {
  optIn: () => posthog.opt_in_capturing(),
  optOut: () => posthog.opt_out_capturing(),
  status: () => posthog.get_explicit_consent_status(),
}

// The recorder, in the same shape and for the same reason as consentApi above: session_replay.js
// decides when to record and never sees posthog itself, so the policy stays testable without a
// browser. `stopSessionRecording` on a session that was never recording is a no-op, which is what
// makes calling it on every ordinary navigation cheap.
const recorder = {
  start: () => posthog.startSessionRecording(),
  stop: () => posthog.stopSessionRecording(),
}

const announceConsentStatus = (status) =>
  document.dispatchEvent(new CustomEvent(CONSENT_STATUS_EVENT, { detail: { status } }))

const markConsentApiReady = wireConsentBridge({
  eventTarget: document,
  consentApi,
  announce: announceConsentStatus,
})

trackTurboPageviews({
  eventTarget: document,
  documentRoot: document,
  // Marking the api ready here is the other half of the handshake, and it is needed because
  // nothing orders the two bundles. A banner whose controller connected before posthog existed
  // asked for the status and got no answer, so the answer is sent the moment there is one, along
  // with any choice made while waiting. Until this runs the bridge stays silent, because posthog
  // reads its consent storage off the project token and answers "pending" to everyone without
  // one (analytics/consent.js). `initialize` runs once per session, which is exactly how often
  // there is a newly-knowable status to announce.
  initialize: (settings) => {
    posthog.init(settings.apiKey, postHogInitOptions(settings))
    markConsentApiReady()
  },
  // The attribution field is filled on the same turbo:load as the pageview, and after init rather
  // than on its own listener, because there is no distinct_id to read until PostHog has started.
  capture: (settings) => {
    // Set before anything is captured off this page, and on every navigation for the reason the
    // recorder is: a session that starts on the marketing site keeps this bundle alive into the
    // signed-in app, so the answer has to be re-asked where the reader now is rather than where
    // they started (analytics/session_replay.js explains what autocapture sends).
    posthog.set_config({ autocapture: autocaptureAllowed({ pageAllowsReplay: settings.sessionReplay }) })
    posthog.capture("$pageview")
    // Decided on every navigation rather than once at init, because the answer changes as the
    // reader moves: the setup wizard allows a recording and the statement they open next does not.
    applyReplayState({
      pageAllowsReplay: settings.sessionReplay,
      optedIn: posthog.has_opted_in_capturing(),
      recorder,
    })
    fillDistinctIdFields({
      documentRoot: document,
      distinctId: posthog.get_distinct_id(),
      optedIn: posthog.has_opted_in_capturing(),
    })
  },
  // A navigation onto a page that renders no settings element at all, which is every page in the
  // product outside setup, settings and the marketing site. Nothing is captured there and, since
  // this is the moment the reader left the allow-list, nothing may still be recording either.
  leave: () => recorder.stop(),
})
